import React, {
  MouseEvent,
  MouseEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { keyframes, ThemeContext } from 'styled-components';
import { hasValue } from '../../common/utils/KitData';
import KitSROnly from '../KitSROnly';
import KitPopover, { Popover } from '../KitPopover';
import useMediaQuery from '../../common/hooks/useMediaQuery';
import { KitFlexRow, KitFlexRowCentered, KitFlexColCentered } from '../KitFlexbox';
import cpLogoSVG from '../../images/cp_logo.svg';
import cpMobileLogo from '../../images/ic_cp_round_logo.png';
import colors from '../../common/theme/colors';
import constants from '../../common/theme/constants';
import HorizontalNavBarItems from './HorizontalNavItems';
import PoweredBy from './PoweredBy';
import VerticalSlidingMenu from './VerticalSlidingNav';
import KitButton from '../KitButton';
import KitDropDownMenu from '../Dropdowns/KitDropDownMenu';
import KitDropDownItem, { DropDownItemProps } from '../Dropdowns/KitDropDownItem';
import KitIcon from '../KitIcon';
import { EventName, TranslateFunc } from '../../common/types';
import { onEscape, onFocusOut as onFocusOutAction } from '../../common/events';

const Container = styled(KitFlexRow)`
  display: flex;
  justify-content: flex-start;
  height: auto;
  min-height: 48px;
  background-color: ${({ theme }) => theme.page.header.bg};

  @media all and (max-width: ${constants.breakpoints.md}px) {
    > div {
      flex: 1;
    }

    /* Selector will only target elements when only the hamburger and the logo are displayed (like in Fleet) 
    This will ignore layouts with children (like the region/station selectors in NOS) */
    > div:first-child:nth-last-child(2),
    > div:first-child:nth-last-child(2) ~ div {
      padding: 0;
      align-items: start;
      img {
        transform: translateX(-50%);
      }
    }
  }
`;

const VerticallyAlignedFlexRow = styled(KitFlexRow)`
  align-items: center;
  height: 100%;
`;

const Logo = styled.img`
  display: block;
  width: auto;
  height: 32px;
`;

const MenuContainer = styled(VerticallyAlignedFlexRow)`
  background-color: ${colors.white};
  padding-left: ${constants.spacing.m}rem;
  display: none;
  @media all and (max-width: ${constants.breakpoints.md}px) {
    align-self: center;
    background-color: ${({ theme }) => theme.page.header.bg};
    display: block;
    padding-left: ${constants.spacing.xxs}rem;
  }
`;

const MenuButton = styled(KitButton)`
  && {
    display: none;
    @media all and (max-width: ${constants.breakpoints.md}px) {
      display: flex;
    }
  }
`;

const LogoContainer = styled(KitFlexColCentered)`
  align-items: center;
  padding-left: ${constants.spacing.m}rem;
  padding-right: ${constants.spacing.m}rem;
  background-color: ${({ theme }) => theme.page.headerLogo.bg};
  & a:focus {
    outline-offset: 2px;
    outline: 2px solid ${({ theme }) => theme.focus};
  }
  @media all and (max-width: ${constants.breakpoints.md}px) {
    background-color: ${({ theme }) => theme.page.header.bg};
    align-items: start;
  }
`;

const AppContainer = styled(VerticallyAlignedFlexRow)`
  height: auto;
  background-color: ${({ theme }) => theme.page.header.border};
  padding-left: ${constants.spacing.s}rem;
  padding-right: ${constants.spacing.s}rem;
  display: flex;
  @media all and (max-width: ${constants.breakpoints.md}px) {
    display: none;
  }
`;

const AppTitle = styled.div`
  font-size: ${constants.fontSize.h4}rem;
  padding-left: ${constants.spacing.m}rem;
  padding-right: ${constants.spacing.m}rem;
  white-space: nowrap;
`;

const AppNavDropdown = styled(KitButton)`
  && {
    color: ${({ theme }) => theme.page.body.text};
    font-size: ${constants.fontSize.h5}rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-right: 0;
    white-space: nowrap;

    &:hover {
      background-color: transparent;
    }

    & * {
      font-family: ${({ theme }) => theme.font.fontFamily};
    }
  }
`;

const fadeInAnimation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// eslint-disable-next-line prettier/prettier
const NavItemsContainer = styled(VerticallyAlignedFlexRow) <{ isEllipsisMenuOpen: boolean }>`
  justify-content: space-between;
  background-color: ${({ theme }) => theme.page.header.bg};
  padding: 0 ${constants.spacing.m}rem;
  display: flex;
  transition: opacity 0.25s ease-in-out;
  animation-name: ${fadeInAnimation};
  animation-duration: 1s;
  overflow: ${(props) => (props.isEllipsisMenuOpen ? 'visible' : 'hidden')};
  height: auto;
  width: 100%;
  @media all and (max-width: ${constants.breakpoints.md}px) {
    display: none;
  }
`;

const UserInfoContainer = styled(VerticallyAlignedFlexRow)`
  padding-top: ${constants.spacing.absolute.s}px;
  padding-bottom: ${constants.spacing.absolute.s}px;
  > .dropdown {
    margin-left: ${constants.spacing.s}rem;
  }

  @media all and (max-width: ${constants.breakpoints.md}px) {
    justify-content: end;
    min-width: max-content;
  }
`;

const CIRCLE_DIAMETER = 2; // rem

const CircleButton = styled(KitButton)`
  && {
    background-color: ${colors.blue_gray_50};
    color: ${colors.white};
    font-weight: ${constants.fontWeight.bold};
    margin-left: ${constants.spacing.absolute.xs}px;

    &:hover {
      background-color: ${colors.blue_gray_70};
    }
  }
`;

const StyledListGroupItem = styled(KitButton)`
  && {
    text-align: left;
    border-radius: 0;
  }
`;

const UserInfoWrapper = styled.div`
  padding: 0 ${constants.spacing.l}rem;
  margin-left: auto;
  [dir='rtl'] & {
    margin-left: initial;
    margin-right: auto;
  }

  @media all and (max-width: ${constants.breakpoints.md}px) {
    padding: 0;
  }
`;

const KeyboardArrowDownIcon = styled(KitIcon)`
  display: flex;
`;

export interface NavLink {
  name: string;
  path: string;
  target?: string;
  onSelect?: (e: MouseEvent<HTMLAnchorElement>) => void;
}
export interface AppLink {
  name: string;
  url?: string;
  onSelect?: (args: AppLink) => void;
}

export interface LogoProps {
  mobileLogoUrl?: string;
  desktopLogoUrl: string;
  altText: string;
  ariaDescription: string;
}

export interface KitNavBarProps {
  className?: string;
  apps?: AppLink[];
  navLinks?: NavLink[];
  activeAppIndex?: number;
  activeNavLinkIndex?: number;
  username: string;
  // When present, userMenuItems will be used in the user menu instead of
  // the default Profile and Logout items.
  userMenuItems?: Pick<DropDownItemProps, 'onClick' | 'label' | 'ref'>[];
  openAppInNewTab?: boolean;
  useReactRouter?: boolean;
  children?: React.ReactNode;
  logoPath?: string;
  onLogoutFunc: () => void;
  onHelpFunc?: () => void;
  onAccountFunc: () => void;
  t: TranslateFunc;
  logo?: LogoProps;
  sortApps?: boolean;
  displayPoweredBy?: boolean;
  displaySingleApp?: boolean;
  displayUserInfo?: boolean;
}

function KitNavBar(props: KitNavBarProps): React.ReactElement {
  const themeContext = useContext(ThemeContext);
  const isMediumScreen = useMediaQuery(`(max-width: ${constants.breakpoints.md}px)`);
  const showNavLinks = Array.isArray(props.navLinks) && props.navLinks.length;
  const displayUserInfo = props.displayUserInfo ?? true;

  const [isEllipsisMenuOpen, setIsEllipsisMenuOpen] = useState(false);
  const [isUserInfoOpen, setIsUserInfoOpen] = useState(false);
  const [isSlidingMenuOpen, setIsSlidingMenuOpen] = useState(false);
  const [isAppMenuOpen, setIsAppMenuOpen] = useState(false);

  const menuBtnRef = useRef(null);
  const initialUserMenuItemRef = useRef(null);
  const userMenuRef = useRef(null);
  const userInfoIconRef = useRef(null);
  const lastFocus = useRef(null);

  // apps refs
  const appsDropdownMenu = useRef(null);
  const currentAppRef = useRef(null);
  const firstAppsRef = useRef(null);
  const lastFocusAppsRef = useRef(null);

  const userMenuItems = (
    props.userMenuItems ?? [
      {
        label: props.t('kit:nav_bar.profile'),
        onClick: props.onAccountFunc,
      },
      {
        label: props.t('kit:nav_bar.logout'),
        onClick: props.onLogoutFunc,
      },
    ]
  ).map((item, index) => {
    if (index === 0) {
      item.ref = initialUserMenuItemRef;
    }
    return item;
  });

  const onKeyPressListener = (event) => onEscape(event, setIsUserInfoOpen, userInfoIconRef.current);
  const onFocusOut = (event) =>
    onFocusOutAction(event, userMenuRef, initialUserMenuItemRef, lastFocus);
  const onAppKeyPressListener = (event) => onEscape(event, setIsAppMenuOpen, currentAppRef.current);
  const onAppFocusOut = (event) =>
    onFocusOutAction(event, appsDropdownMenu, firstAppsRef, lastFocusAppsRef);

  useEffect(() => {
    if (menuBtnRef.current && !isSlidingMenuOpen) {
      menuBtnRef.current.focus();
    }
  }, [isSlidingMenuOpen]);

  useEffect(() => {
    const removeUserListeners = () => {
      document.removeEventListener(EventName.KEYUP, onKeyPressListener);
      document.removeEventListener(EventName.FOCUSOUT, onFocusOut, true);
      lastFocus.current = null; // forget last focus when menu is closed
    };

    if (isUserInfoOpen) {
      initialUserMenuItemRef.current.focus();
      document.addEventListener(EventName.KEYUP, onKeyPressListener);
      document.addEventListener(EventName.FOCUSOUT, onFocusOut, true);
    } else {
      removeUserListeners();
    }
    return () => {
      removeUserListeners();
    };
  }, [isUserInfoOpen]);

  useEffect(() => {
    const removeAppsListeners = () => {
      document.removeEventListener(EventName.KEYUP, onAppKeyPressListener);
      document.removeEventListener(EventName.FOCUSOUT, onAppFocusOut, true);
      lastFocusAppsRef.current = null; // forget last focus when menu is closed
    };

    if (isAppMenuOpen) {
      firstAppsRef.current.focus();
      document.addEventListener(EventName.KEYUP, onAppKeyPressListener);
      document.addEventListener(EventName.FOCUSOUT, onAppFocusOut, true);
    } else {
      removeAppsListeners();
    }
    return () => {
      removeAppsListeners();
    };
  }, [isAppMenuOpen]);

  if (showNavLinks && props.navLinks.length === 0) {
    console.error('Invalid navLinks property');
    return null;
  }

  const onMenuClicked = () => {
    setIsSlidingMenuOpen(true);
  };

  const renderAppsDropdown = () => {
    // Per specs we should hide the AppSwitcher if there's only 1 app. Just show a title here.
    if (props.apps.length === 1) {
      return <AppTitle>{props.apps[0].name}</AppTitle>;
    }

    // Treat an empty array as an error!
    if (props.apps.length === 0) {
      console.error('Invalid apps array');
      return null;
    }

    // Otherwise render an app switcher dropdown
    let currentAppTitle = '';

    if (props.sortApps) {
      props.apps.sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    const dropdownItems = props.apps.map((app, i) => {
      const isActive: boolean =
        (hasValue(props.activeAppIndex) && props.activeAppIndex === i) ||
        window.location.origin === app.url;

      if (isActive) {
        currentAppTitle = app.name;
      }
      const menuItemProps = {
        active: isActive,
        href: app.url,
        onClick: app.onSelect ? () => app.onSelect(app) : undefined,
        target: props.openAppInNewTab ? '_blank' : '_self',
      };

      return (
        <KitDropDownItem
          key={app.name}
          active={isActive}
          ref={i === 0 ? firstAppsRef : null}
          {...menuItemProps}
        >
          {app.name}
        </KitDropDownItem>
      );
    });

    return (
      <KitPopover
        isOpen={isAppMenuOpen}
        positions={['bottom']}
        align='start'
        onClickOutside={() => {
          setIsAppMenuOpen(false);
        }}
        content={
          <KitPopover.Container>
            <KitDropDownMenu ref={appsDropdownMenu}>{dropdownItems}</KitDropDownMenu>
          </KitPopover.Container>
        }
      >
        <KitPopover.Trigger>
          <AppNavDropdown
            ref={currentAppRef}
            variant='link'
            onClick={() => setIsAppMenuOpen(!isAppMenuOpen)}
          >
            <span>{currentAppTitle}</span>{' '}
            <KeyboardArrowDownIcon icon='angle-down' fill={colors.gray_70} />
          </AppNavDropdown>
        </KitPopover.Trigger>
      </KitPopover>
    );
  };

  const userMenu = (
    <KitDropDownMenu ref={userMenuRef}>
      {userMenuItems.map((item) => (
        <KitDropDownItem key={`${item.label}`} onClick={item.onClick} ref={item.ref}>
          {item.label}
        </KitDropDownItem>
      ))}
    </KitDropDownMenu>
  );

  const renderChildren = () => {
    // Desktop w/ Powered By
    if (props.displayPoweredBy && !isMediumScreen) {
      return (
        <KitFlexRow>
          <PoweredBy t={props.t} />
          {props.children}
        </KitFlexRow>
      );
    }

    return props.children;
  };

  const renderUserInfo = () => {
    const userName = props.username.split(' ');
    let userInitials = userName[0][0];
    if (userName.length > 1) {
      userInitials = userName[0][0] + userName[1][0];
    }

    if (!isMediumScreen) {
      // Desktop
      return (
        <UserInfoContainer>
          {renderChildren()}
          {props.onHelpFunc && (
            <CircleButton
              diameter={CIRCLE_DIAMETER}
              variant='circle'
              onClick={props.onHelpFunc}
              aria-label={props.t('kit:nav_bar.help')}
            >
              ?
            </CircleButton>
          )}

          <Popover
            reposition
            isOpen={isUserInfoOpen}
            positions={['bottom', 'right', 'left', 'top']}
            padding={10}
            ref={userInfoIconRef}
            onClickOutside={() => {
              setIsUserInfoOpen(false);
              setIsEllipsisMenuOpen(false);
            }}
            content={userMenu}
            align='start'
            containerStyle={{ zIndex: '1' }}
          >
            <CircleButton
              variant='circle'
              diameter={CIRCLE_DIAMETER}
              onClick={() => {
                setIsUserInfoOpen(!isUserInfoOpen);
                setIsEllipsisMenuOpen(!isUserInfoOpen);
              }}
              aria-label={props.t('kit:nav_bar.profile')}
            >
              {userInitials}
            </CircleButton>
          </Popover>
        </UserInfoContainer>
      );
    }

    // On mobile, only render the user info container if there are children (like in NOS)
    // Rendering this container without children was resulting in an empty div rendering with no purpose
    return props.children ? (
      <UserInfoWrapper>
        <UserInfoContainer>{renderChildren()}</UserInfoContainer>
      </UserInfoWrapper>
    ) : null;
  };

  const renderLogo = ({ altText, url }) => {
    return <Logo src={url} alt={altText} />;
  };

  const render = () => {
    let elements;
    let logoURL = props.logo?.desktopLogoUrl ?? cpLogoSVG;
    const logoAlt = props.logo?.altText ?? props.t('kit:nav_bar.alt.header_logo');
    const logoDescription = props.logo?.ariaDescription ?? props.t('kit:nav_bar.aria.logo_help');
    const logoPath = showNavLinks ? props.navLinks[0].path : props.logoPath;
    let showAppSwitcher = Array.isArray(props.apps) && props.apps.length > 0;

    // If there's only 1 item in app switcher array we may want to hide it completely.
    if (props.apps?.length === 1 && !props.displaySingleApp) {
      showAppSwitcher = false;
    }

    if (isMediumScreen) {
      logoURL = props.logo?.mobileLogoUrl ?? cpMobileLogo;
      // Tablet or smaller (logo is CENTERED)
      // menu <-----> logo <------> empty
      elements = (
        <>
          <MenuContainer>
            <MenuButton
              onClick={onMenuClicked}
              variant='link'
              ref={menuBtnRef}
              aria-label={props.t('kit:nav_bar.alt.toggle_menu')}
            >
              <KitIcon
                fill={themeContext.button.primary.normal.text}
                icon='menu'
                size={`${constants.iconSize.m}rem`}
              />
            </MenuButton>
          </MenuContainer>
          <LogoContainer className='logo-container mobile'>
            <KitSROnly id='aria-logo-description' aria-hidden='true'>
              {logoDescription}
            </KitSROnly>
            <a href={logoPath} aria-describedby='aria-logo-description'>
              {renderLogo({ altText: logoAlt, url: logoURL })}
            </a>
          </LogoContainer>
          {displayUserInfo && renderUserInfo()}
        </>
      );
    } else {
      // Desktop
      // logo | app switcher | nav links <-------> user info
      elements = (
        <>
          <LogoContainer>
            <KitSROnly id='aria-logo-description' aria-hidden='true'>
              {logoDescription}
            </KitSROnly>
            <a href={logoPath} aria-describedby='aria-logo-description'>
              {renderLogo({ altText: logoAlt, url: logoURL })}
            </a>
          </LogoContainer>
          {showAppSwitcher && <AppContainer>{renderAppsDropdown()}</AppContainer>}
          <NavItemsContainer isEllipsisMenuOpen={isEllipsisMenuOpen}>
            <HorizontalNavBarItems
              {...props}
              isMenuOpen={(isOpen) => {
                setIsEllipsisMenuOpen(isOpen);
              }}
            />
            {displayUserInfo && renderUserInfo()}
          </NavItemsContainer>
        </>
      );
    }

    return <Container className={props.className}>{elements}</Container>;
  };

  let poweredBy = null;
  if (props.displayPoweredBy) {
    poweredBy = (
      <>
        <hr />
        <KitFlexRowCentered>
          <PoweredBy t={props.t} />
        </KitFlexRowCentered>
      </>
    );
  }

  return (
    <>
      <VerticalSlidingMenu
        {...props}
        show={isSlidingMenuOpen}
        hideMe={() => {
          setIsSlidingMenuOpen(false);
        }}
      >
        {props.onHelpFunc && (
          <StyledListGroupItem
            variant='link'
            onClick={props.onHelpFunc}
            tabIndex={isSlidingMenuOpen ? undefined : -1}
          >
            {props.t('kit:nav_bar.help')}
          </StyledListGroupItem>
        )}

        {userMenuItems.map((item) => (
          <StyledListGroupItem
            ref={item.ref}
            onClick={item.onClick as unknown as MouseEventHandler<HTMLButtonElement>}
            variant='link'
          >
            {item.label}
          </StyledListGroupItem>
        ))}

        {poweredBy}
      </VerticalSlidingMenu>
      {render()}
    </>
  );
}

KitNavBar.defaultProps = {
  activeNavLinkIndex: 0,
  openAppInNewTab: false,
  sortApps: false,
  displayPoweredBy: false,
  displaySingleApp: false,
};

export default KitNavBar;
